import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Button, Modal, Spin, Empty } from "antd";
import { toast } from "../../components/toasts/toastStore";
import { apiGastosInstance, authHeaderGastos } from "../../redux/actions/gastos/gastos";

import "./GastosPage.css";

dayjs.locale("es");

const fmtFechaCorta = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("D MMM YYYY, HH:mm") : "—";
};

const fmtDatetime = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD/MM/YYYY, HH:mm") : "—";
};

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const ACTION_CONFIG = {
  CREATE: { label: "Creacion", color: "#15803d", bg: "#dcfce7" },
  UPDATE: { label: "Actualizacion", color: "#f90336", bg: "#fee2e6" },
  DELETE: { label: "Eliminacion", color: "#b91c1c", bg: "#fee2e2" },
};
const getActionConfig = (a) => ACTION_CONFIG[a] || { label: a || "Cambio", color: "#595c5e", bg: "#f1f5f9" };

export default function GastoDetallePage() {
  const { idGasto } = useParams();
  const navigate = useNavigate();

  const [gasto, setGasto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [actividad, setActividad] = useState([]);
  const [loadingActividad, setLoadingActividad] = useState(true);

  const fetchGasto = useCallback(() => {
    setLoading(true);
    apiGastosInstance.get(`/gastos/${idGasto}`, { headers: authHeaderGastos() })
      .then((res) => setGasto(res.data))
      .catch(() => toast.error("No se pudo cargar el gasto"))
      .finally(() => setLoading(false));
  }, [idGasto]);

  useEffect(() => { fetchGasto(); }, [fetchGasto]);

  useEffect(() => {
    setLoadingActividad(true);
    apiGastosInstance.get(`/gastos/${idGasto}/actividad`, { headers: authHeaderGastos() })
      .then((res) => setActividad(res.data || []))
      .catch(() => {})
      .finally(() => setLoadingActividad(false));
  }, [idGasto]);

  const handleDelete = () => {
    Modal.confirm({
      title: "Eliminar este gasto?",
      content: "Esta accion no se puede deshacer.",
      okText: "Eliminar",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        setDeleting(true);
        try {
          await apiGastosInstance.delete(`/gastos/${idGasto}`, { headers: authHeaderGastos() });
          toast.success("Gasto eliminado");
          navigate("/app/gastos");
        } catch (err) {
          toast.error(err?.response?.data?.detail || "No se pudo eliminar");
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  if (loading) {
    return (
      <main className="gas-main" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <Spin size="large" />
      </main>
    );
  }

  if (!gasto) {
    return (
      <main className="gas-main" style={{ padding: 40 }}>
        <Empty description="Gasto no encontrado" />
      </main>
    );
  }

  return (
    <main className="gas-main">
      <div className="gas-content">
        <div className="gas-section-card">
          <div className="gas-detail-card-header">
            <div className="gas-detail-card-title-col">
              <Title>{gasto.concepto}</Title>
              <span style={{ color: "#94a3b8", fontSize: 13 }}>Gasto #{gasto.id_gasto}</span>
            </div>
            <div className="gas-detail-card-actions">
              <Button icon={<i className="fa-solid fa-pen" />} onClick={() => navigate("/app/gastos/crear", { state: { gasto } })}>
                Editar
              </Button>
              <Button danger icon={<i className="fa-solid fa-trash" />} loading={deleting} onClick={handleDelete}>
                Eliminar
              </Button>
            </div>
          </div>

          <div className="gas-detail-monto-row">
            <span className="gas-info-monto">{money(gasto.monto)}</span>
          </div>

          <div className="gas-detail-info-grid">
            <div className="gas-info-block">
              <div className="gas-info-label">Fecha del gasto</div>
              <div className="gas-info-value">{gasto.fecha}</div>
            </div>
            <div className="gas-info-block">
              <div className="gas-info-label">Registrado por</div>
              <div className="gas-info-value">{gasto.usuario_nombre || "—"}</div>
            </div>
            <div className="gas-info-block">
              <div className="gas-info-label">Fecha de creacion</div>
              <div className="gas-info-value">{fmtFechaCorta(gasto.datetime)}</div>
            </div>
          </div>

          {gasto.nota && (
            <div className="gas-notas-block">
              <div className="gas-info-label" style={{ marginBottom: 6 }}>Nota</div>
              <div>{gasto.nota}</div>
            </div>
          )}

          <div className="gas-detail-divider" />

          <div className="gas-section-header">
            <span className="gas-section-icon"><i className="fa-solid fa-clock" /></span>
            Actividad
          </div>
          {loadingActividad ? <Spin /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {actividad.map((item, idx) => {
                const cfg = getActionConfig(item.action);
                return (
                  <div key={item.id_audit_log || idx} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <span style={{ padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700, color: cfg.color, background: cfg.bg }}>{cfg.label}</span>
                    <div>
                      <div style={{ fontSize: 13, color: "#3d0011" }}>{item.message}</div>
                      <div style={{ fontSize: 11, color: "#94a3b8" }}>{fmtDatetime(item.datetime)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

function Title({ children }) {
  return <div style={{ fontSize: 20, fontWeight: 700, color: "#3d0011" }}>{children}</div>;
}
